import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ItemRow } from "@/components/InvoiceEditor";
import { Plus } from "lucide-react";

const money = (v) => (Math.round(v * 100) / 100).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const gross = (items) => items.reduce((a, i) => {
  const net = Math.round((+i.qty || 0) * (+i.unit_net || 0) * 100) / 100;
  return a + net + (/^\d+$/.test(i.vat_rate) ? Math.round(net * +i.vat_rate) / 100 : 0);
}, 0);
const strip = (i) => ({ name: i.name, qty: i.qty, unit: i.unit, unit_net: i.unit_net, vat_rate: i.vat_rate });

export function CorrectionDialog({ inv, onClose, onDone }) {
  const [before, setBefore] = useState(null);
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState("");
  const [issue, setIssue] = useState(new Date().toLocaleDateString("sv-SE"));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const full = (await api.get(`/invoices/${inv.invoice_id}`)).data;
      const src = full.current_items || full.items;
      setBefore(src.map(strip)); setItems(src.map(strip));
    })().catch(() => toast.error("Nie udało się wczytać faktury"));
  }, [inv.invoice_id]);

  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/invoices/${inv.invoice_id}/correct`, { reason, issue_date: issue, items: items.map((i) => ({ ...i, qty: +i.qty || 0, unit_net: +i.unit_net || 0 })) });
      toast.success(`Wystawiono korektę ${r.data.number}`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Błąd"); } finally { setBusy(false); }
  };
  const diff = before ? gross(items) - gross(before) : 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto" data-testid="correction-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Faktura korygująca do {inv.number}</DialogTitle>
          <DialogDescription>Numer KSeF: {inv.ksef?.ksef_number}. Popraw pozycje — zapiszemy stan przed i po korekcie (seria KOR).</DialogDescription>
        </DialogHeader>
        {!before ? <p className="text-sm text-muted-foreground">Wczytywanie…</p> : (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-2">
              <div className="sm:col-span-2"><Label>Przyczyna korekty</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="np. błędna cena, rabat, zwrot" data-testid="correction-reason" /></div>
              <div><Label>Data wystawienia</Label><Input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="correction-issue-date" /></div>
            </div>
            <Label>Pozycje po korekcie</Label>
            {items.map((it, i) => <ItemRow key={i} it={it} i={i} onChange={(v) => setItems(items.map((x, j) => (j === i ? v : x)))} onRemove={() => setItems(items.filter((_, j) => j !== i))} />)}
            <Button variant="outline" size="sm" onClick={() => setItems([...items, { name: "", qty: 1, unit: "usł.", unit_net: 0, vat_rate: items[0]?.vat_rate || "23" }])} data-testid="correction-add-item"><Plus className="h-3.5 w-3.5 mr-1" />Dodaj pozycję</Button>
            <p className="text-sm text-right" data-testid="correction-diff">Przed: {money(gross(before))} · Po: {money(gross(items))} · <strong className={diff < 0 ? "text-red-600" : "text-[#193962]"}>Różnica {money(diff)} {inv.currency}</strong></p>
            <Button onClick={go} disabled={busy || !reason.trim()} className="w-full bg-[#193962] text-white" data-testid="correction-submit">{busy ? "Wystawianie…" : "Wystaw fakturę korygującą"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
